import { noop } from 'lodash'
import { useState } from 'react'
import DOMPurify from 'dompurify'
import {
  Button,
  Box,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerCloseButton,
  DrawerBody,
  FormControl,
  FormLabel,
  Stack,
  Textarea,
  useDisclosure,
  useToast,
  Input
} from '~/design_system'
import { useContactUsConfigQuery, useContactUsMutation } from '~/api/home'
import { Envelope, MessageBubble } from '~/icons'
import { useMsg, Msg } from '~/common/localizationUtils'
import { trackedButtonClick } from '~/third_party/analytics'

export const ContactUsDrawer = () => {
  const { data } = useContactUsConfigQuery()

  const { isOpen, onOpen, onClose } = useDisclosure()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')

  const { mutateAsync: sendMessage } = useContactUsMutation()
  const toast = useToast()
  const msg = useMsg()

  const onSubmit = async () => {
    const { message: responseMessage, status } = (await sendMessage({ query: message, name, email }).catch(noop)) ?? {}
    toast({
      position: 'bottom-right',
      title: responseMessage,
      status,
      duration: 9000,
      isClosable: true
    })

    if (status === 'success') {
      onClose()
      setName('')
      setEmail('')
      setMessage('')
    }
  }

  const sanitize = (dirty: string) => {
    // Adding a hook to open links in new window if set
    DOMPurify.addHook('afterSanitizeAttributes', node => {
      // if a link has been set to open in new window, set target=_blank
      if (node.hasAttribute('rel')) {
        node.setAttribute('target', '_blank')
        node.setAttribute('rel', 'noopener noreferrer')
      }
    })
    return {
      __html: DOMPurify.sanitize(dirty, { USE_PROFILES: { html: true } })
    }
  }

  return data?.enabled ? (
    <Box sx={{ '@media print': { display: 'none' } }}>
      <Button
        leftIcon={<Envelope />}
        position="fixed"
        left="100%"
        bottom={30}
        roundedBottom={0}
        transformOrigin="0% 0%"
        transform="rotate(-90deg) translateY(-100%)"
        mr="-45px"
        variant="primary"
        zIndex="docked"
        whiteSpace="nowrap"
        data-testid="contact-us-tab-button"
        onClick={trackedButtonClick(onOpen, 'contact_us')}>
        <Msg id="contact_us.drawer.heading" />
      </Button>
      <Drawer isOpen={isOpen} placement="right" size="sm" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent data-testid="contact-us-drawer">
          <DrawerHeader bg="system.color.bg.primary.default" color="white">
            <Stack direction="row" alignItems="center">
              <Box as={MessageBubble} alignSelf="center" />
              <Box as="span">
                <Msg id="contact_us.drawer.heading" />
              </Box>
            </Stack>
          </DrawerHeader>
          <DrawerCloseButton color="white" aria-label={msg('common.ui.close')} />
          <DrawerBody>
            <Stack spacing={4} mt={4} h="100%" justifyContent="space-between">
              {data?.custom_message && (
                <Box
                  dangerouslySetInnerHTML={sanitize(data?.custom_message)}
                  sx={{
                    a: {
                      color: 'var(--vndly-color-link)',
                      _hover: {
                        color: 'var(--vndly-color-link-hover)',
                        bgColor: 'var(--vndly-color-link-background-hover)'
                      }
                    }
                  }}
                />
              )}
              {data?.hide_form ? (
                <Button w="fit-content" onClick={onClose}>
                  <Msg id="common.ui.close" />
                </Button>
              ) : (
                <>
                  <FormControl isRequired>
                    <Stack>
                      {data?.request_user_info && (
                        <>
                          <FormLabel htmlFor="name" fontWeight="semibold">
                            <Msg id="contact_us.drawer.name.label" />
                          </FormLabel>
                          <Input
                            id="name"
                            placeholder={msg('contact_us.drawer.name.placeholder')}
                            onChange={e => setName(e.target.value)}
                            value={name}
                          />
                          <FormLabel htmlFor="email" fontWeight="semibold">
                            <Msg id="contact_us.drawer.email.label" />
                          </FormLabel>
                          <Input
                            id="email"
                            placeholder={msg('contact_us.drawer.email.placeholder')}
                            onChange={e => setEmail(e.target.value)}
                            value={email}
                          />
                        </>
                      )}
                      <FormLabel htmlFor="message" fontWeight="semibold">
                        <Msg id="contact_us.drawer.message.label" />
                      </FormLabel>
                      <Textarea
                        id="message"
                        placeholder={msg('contact_us.drawer.message.placeholder')}
                        onChange={e => setMessage(e.target.value)}
                        value={message}
                        height="200px"
                        resize="none"
                      />
                    </Stack>
                  </FormControl>
                  <Button
                    variant="primary"
                    w="fit-content"
                    isDisabled={message === ''}
                    onClick={trackedButtonClick(onSubmit, 'send_message')}>
                    <Msg id="contact_us.drawer.send.button" />
                  </Button>
                </>
              )}
            </Stack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  ) : null
}
